
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import {
	DEFAULT_SHOW_TEXT_COUNTER_AT,
	DEFAULT_TEXT_MAX_LENGTH,
	getTextMaxLengthRule,
} from '@/utils';
import BackButton from '@/components/base/BackButton.vue';
import { db } from '@/firebase';
import {
	AppOffer,
	HardwareOffer,
	ClubKey,
	ClubOffer,
	centsToDollars,
	dollarsToCents,
} from '@run-diffusion/shared';
import _find from 'lodash/find';
import _trim from 'lodash/trim';
import _values from 'lodash/values';
import _isEmpty from 'lodash/isEmpty';
import HtmlField from '@/components/HtmlField.vue';
import StringArrayField from '@/components/StringArrayField.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import HardwareOfferCard from '@/views/Sessions/HardwareOffersList/HardwareOfferCard.vue';
import { ROUTER } from '@/router/constants';
import NumberField from '@/components/base/NumberField.vue';

export default Vue.extend({
	name: 'AdminEditHardwareOffers',
	metaInfo: {
		title: 'Edit Hardware Offers | Admin | RunDiffusion',
	},
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			DEFAULT_SHOW_TEXT_COUNTER_AT,
			DEFAULT_TEXT_MAX_LENGTH,

			createNewHardwareOfferKey: null,
			localHardwareOffers: [],
			hasMovedHardwareOfferItem: false,
			editHardwareOffer: null,
			expandedHardwareOffer: null,
			orderedAppKeys: [],
			availableClubs: [] as ClubKey[],

			isCreatingNew: false,
			duplicateHardwareOffer: false,

			saving: false,
			formValid: false,
			rules: {
				requiredTextField: [
					getTextMaxLengthRule(),
					v => !!_trim(v) || 'Required',
				],
				requiredNumber: [
					v => !!v || v === 0 || 'Required',
					v => !isNaN(parseFloat(v)) && isFinite(v) || 'Must be a number',
				],
				percentage: [
					v => !isNaN(parseFloat(v)) && isFinite(v) || 'Must be a number',
					v => v >= 0 && v <= 100 || 'Must be between 0 and 100',
				],
				required: [
					v => !!_trim(v) || 'Required',
				],
				requiredArray: [
					v => !!v.length || 'Required',
				],
			},
		};
	},
	computed: {
		...mapState([
			'user',
			'hardwareOffers',
			'appOffers',
			'clubOffers',
		]),
	},
	watch: {
		appOffers: {
			immediate: true,
			handler (newVal: AppOffer[], oldVal: AppOffer[]) {
				if (newVal !== oldVal) {
					this.orderedAppKeys = (newVal || []).map(({ app }) => app).sort();
				}
			},
		},
		clubOffers: {
			immediate: true,
			handler (newVal: ClubOffer[], oldVal: ClubOffer[]) {
				if (newVal !== oldVal) {
					this.availableClubs = (newVal || []).map(({ club }) => club).sort();
				}
			},
		},
		hardwareOffers: {
			immediate: true,
			handler (newVal: HardwareOffer[], oldVal: HardwareOffer[]) {
				if (newVal !== oldVal) {
					this.localHardwareOffers = (newVal || []).map(this.copyLocalHardwareOffer);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		_values,
		_trim,
		centsToDollars,
		dollarsToCents,
		goBack () {
			if (window.history.length > 2) {
				this.$router.back();
			} else {
				const hasCurrentSessionId: boolean = !!(this._get(this.toolbar, 'session.id'));

				if (hasCurrentSessionId) {
					this.routerPush(this.$route, this.$router, { name: ROUTER.LAUNCH, params: { sessionId: this._get(this.toolbar, 'session.id') } });
				} else {
					this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
				}
			}
		},
		onCreateNewClick (hardwareOffer: HardwareOffer) {
			this.isCreatingNew = true;
			this.duplicateHardwareOffer = hardwareOffer || null;
			this.createNewHardwareOfferKey = hardwareOffer ? hardwareOffer.hardware : null;
		},
		onEditHardwareOfferCancel () {
			if (this.editHardwareOffer) {
				const valueHardwareOffer: HardwareOffer = _find(
					this.value || [],
					({ id }) => id === this.editHardwareOffer.id,
				) || null;
				if (valueHardwareOffer) {
					this.localHardwareOffers = this.localHardwareOffers.map((localHardwareOffer: HardwareOffer) => {
						if (localHardwareOffer.id !== this.editHardwareOffer.id) {
							return localHardwareOffer;
						}
						return this.copyLocalHardwareOffer(valueHardwareOffer);
					});
				}

				this.editHardwareOffer = null;
			}
		},
		copyLocalHardwareOffer (hardwareOffer: HardwareOffer) {
			const clubDiscounts = hardwareOffer.clubDiscounts ?? {};
			return {
				id: hardwareOffer.id,
				publishedAt: hardwareOffer.publishedAt ? new Date(hardwareOffer.publishedAt.toMillis()) : null,
				isTeam: !!hardwareOffer.isTeam,
				hardware: hardwareOffer.hardware,
				price: hardwareOffer.price,
				label: hardwareOffer.label,
				shortLabel: hardwareOffer.shortLabel,
				sortOrder: hardwareOffer.sortOrder,
				apps: (hardwareOffer.apps || []).sort(),
				clubs: (hardwareOffer.clubs || []).sort(),
				teamDiscount: hardwareOffer.teamDiscount || {},
				clubDiscounts,
				descriptionHtml: hardwareOffer.descriptionHtml || null,
			};
		},
		moveHardwareOfferItem (fromIndex: number, toIndex: number) {
			if (toIndex <= (this.localHardwareOffers || []).length - 1 && toIndex >= 0) {
				const item: HardwareOffer = this.localHardwareOffers[fromIndex];
				let newArray: HardwareOffer[] = this.localHardwareOffers.filter((val: HardwareOffer) => val !== item);
				newArray.splice(toIndex, 0, item);
				this.localHardwareOffers = newArray;
				this.hasMovedHardwareOfferItem = true;
			}
		},
		async onCreateNew (closeCallback: Function) {
			const newHardwareKey: string = _trim(this.createNewHardwareOfferKey).toUpperCase();
			if (this.saving || !newHardwareKey) return;

			try {
				this.saving = true;
				const hardwareOfferRef = db
					.doc(`hardwareOffers/${newHardwareKey}`);

				// Make sure key is free
				if ((await hardwareOfferRef.get()).exists) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `The hardware key "${newHardwareKey}" is already being used`,
						show: true,
					});
				} else {
					await hardwareOfferRef.set({
						hardware: newHardwareKey,
						publishedAt: null,
						sortOrder: this.localHardwareOffers.length,
						...(this.duplicateHardwareOffer ? {
							isTeam: !!this.duplicateHardwareOffer.isTeam,
							price: this.duplicateHardwareOffer.price,
							label: `${this.duplicateHardwareOffer.label} (duplicated)`,
							shortLabel: this.duplicateHardwareOffer.shortLabel,
							apps: this.duplicateHardwareOffer.apps || [],
							clubs: this.duplicateHardwareOffer.clubs || [],
							teamDiscount: this.duplicateHardwareOffer.teamDiscount || { percent: 0 },
							clubDiscounts: this.duplicateHardwareOffer.clubDiscounts ? { ...this.duplicateHardwareOffer.clubDiscounts } : {},
							descriptionHtml: this.duplicateHardwareOffer.descriptionHtml,
						} : {
							label: '',
							shortLabel: '',
							price: 0,
							descriptionHtml: '',
							apps: [],
							clubs: [],
						}),
					});

					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Hardware offer created.`,
						show: true,
					});
					this.createNewHardwareOfferKey = null;
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error creating hardware offer`,
					show: true,
				});
			} finally {
				if (closeCallback) closeCallback();
				this.saving = false;
			}
		},
		async onSaveHardwareOffersSortOrder () {
			if (this.saving) return;
			try {
				this.saving = true;
				await Promise.all(this.localHardwareOffers.map(async (hardwareOffer: HardwareOffer, index: number) => {
					const hardwareOfferRef = db
						.doc(`hardwareOffers/${hardwareOffer.id}`);
					await hardwareOfferRef.update({
						sortOrder: index,
					});
				}));
				this.hasMovedHardwareOfferItem = false;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Sort order saved.`,
					show: true,
				});
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving sort order',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		async onSave () {
			if (!this.editHardwareOffer || this.saving) return;
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			try {
				this.saving = true;
				const hardwareOfferRef = db
					.doc(`hardwareOffers/${this.editHardwareOffer.id}`);

				// Build club discounts object
				const clubDiscounts: any = {};
				this.availableClubs.forEach((club: ClubKey) => {
					if (this.editHardwareOffer.clubDiscounts?.[club]?.percent > 0) {
						clubDiscounts[club] = {
							percent: this.editHardwareOffer.clubDiscounts[club].percent
						};
					}
				});

				// Build team discounts object
				const teamDiscount: any = {};
				if (this.editHardwareOffer.teamDiscount?.percent > 0) {
					teamDiscount.percent = this.editHardwareOffer.teamDiscount.percent;
				}

				await hardwareOfferRef.update({
					publishedAt: this.editHardwareOffer.publishedAt,
					isTeam: !!this.editHardwareOffer.isTeam,
					price: this.editHardwareOffer.price,
					label: _trim(this.editHardwareOffer.label),
					shortLabel: _trim(this.editHardwareOffer.shortLabel),
					apps: this.editHardwareOffer.apps.map((v: string) => _trim(v).toUpperCase()),
					clubs: this.editHardwareOffer.clubs.map((v: string) => _trim(v).toUpperCase()),
					...(!_isEmpty(teamDiscount) && {
						teamDiscount,
					}),
					...(!_isEmpty(clubDiscounts) && {
						clubDiscounts,
					}),
					descriptionHtml: _trim(this.editHardwareOffer.descriptionHtml),
				});

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! ${this.editHardwareOffer.label} saved.`,
					show: true,
				});
				this.editHardwareOffer = null;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error saving hardware offer ${this.editHardwareOffer.label}`,
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		getTeamDiscountPercent () {
			if (this.editHardwareOffer.teamDiscount?.percent > 0) {
				return this.editHardwareOffer.teamDiscount.percent;
			}
			return 0;
		},
		updateTeamDiscountPercent (value: number) {
			if (!this.editHardwareOffer.teamDiscount) {
				this.editHardwareOffer.teamDiscount = { percent: 0 };
			}
			this.editHardwareOffer.teamDiscount.percent = value;
		},
		getClubDiscountPercent (club: ClubKey) {
			if (this.editHardwareOffer.clubDiscounts?.[club]?.percent > 0) {
				return this.editHardwareOffer.clubDiscounts[club].percent;
			}
			return 0;
		},
		updateClubDiscountPercent (club: ClubKey, value: number) {
			if (!this.editHardwareOffer.clubDiscounts) {
				this.editHardwareOffer.clubDiscounts = {};
			}
			if (!this.editHardwareOffer.clubDiscounts[club]) {
				this.editHardwareOffer.clubDiscounts[club] = { percent: 0 };
			}
			this.editHardwareOffer.clubDiscounts[club].percent = value;
		},
		formatApps (apps: string[]) {
			if (!apps || !apps.length) return '';
			return apps.map((app, i) => (i > 0 && i % 5 === 0) ? `<br>${app}` : app).join(', ');
		},
	},
	components: {
		HardwareOfferCard,
		ConfirmDialog,
		ActionsIsland,
		HtmlField,
		StringArrayField,
		BackButton,
		NumberField,
	},
});
